// API配置
const API_BASE = 'https://cloudflare-mutimodal-backend.steven-mei0814.workers.dev'

// 认证相关的导入
import { useAuthStore } from '../stores/auth'

// 类型定义从 api-types.ts 导入并重新导出
export type {
  APIResponse,
  GenerationResult,
  HistoryResult,
  SpeechResult,
  UserProfile,
  UserQuota,
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  UpdateProfileRequest,
} from './api-types'

import type {
  APIResponse,
  GenerationResult,
  HistoryResult,
  SpeechResult,
  UserProfile,
  UserQuota,
  RegisterRequest,
  AuthResponse,
  UpdateProfileRequest,
} from './api-types'

export class APIClient {
  private baseUrl: string

  constructor(baseUrl: string = API_BASE) {
    this.baseUrl = baseUrl
  }

  // 获取认证头
  private getAuthHeaders(): Record<string, string> {
    const { token } = useAuthStore.getState()

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
      console.log(`[API] Adding auth header with token: ${token.substring(0, 15)}... (length: ${token.length})`);
    } else {
      console.log(`[API] No token found in auth store`);
    }

    return headers
  }

  // 通用API请求方法
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`
    const headers = this.getAuthHeaders()

    const config: RequestInit = {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    }

    let response: Response

    try {
      response = await fetch(url, config)
    } catch (error) {
      console.error('Network error:', error)
      throw new Error('网络连接失败，请检查网络连接')
    }

    // 处理认证错误 - 更宽容的处理
    if (response.status === 401) {
      const authState = useAuthStore.getState()
      
      console.warn(`[API] 401 for ${config.method} ${endpoint}`)

      // 对于内部系统，采用更宽容的策略
      // 只在明确的登录操作失败时才logout
      const isUserProfileUpdate = url.includes('/api/user/profile') && config.method === 'PUT'
      
      // 只有在明确的用户操作且非自动检查时才logout
      if (isUserProfileUpdate || (config.method === 'POST' && !url.includes('/api/auth/') && !url.includes('/api/user/quota'))) {
        console.log(`[API] Critical operation failed auth, logging out`)
        const { logout } = authState
        await logout()
        throw new Error('认证已过期，请重新登录')
      } else {
        // 其他情况，仅抛出错误但保持登录状态
        console.warn(`[API] Request failed auth but keeping session`)
        throw new Error('请求暂时失败，请稍后重试')
      }
    }

    // 处理其他HTTP错误
    if (!response.ok) {
      let errorMessage = `请求失败: ${response.status}`

      try {
        const errorData = await response.json()
        errorMessage = errorData.error || errorData.message || errorMessage
      } catch {
        // 如果无法解析错误响应，使用默认消息
      }

      throw new Error(errorMessage)
    }

    return await response.json()
  }

  // ============ 通用 HTTP 方法 ============

  async get<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // ============ 认证相关API ============

  async login(email: string, password: string): Promise<APIResponse<AuthResponse>> {
    return this.request<APIResponse<AuthResponse>>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
  }

  async register(data: RegisterRequest): Promise<APIResponse<{ message: string }>> {
    return this.request<APIResponse<{ message: string }>>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async logout(): Promise<APIResponse<{ message: string }>> {
    return this.request<APIResponse<{ message: string }>>('/api/auth/logout', {
      method: 'POST',
    })
  }

  async getCurrentUser(): Promise<APIResponse<UserProfile>> {
    return this.request<APIResponse<UserProfile>>('/api/auth/me')
  }

  async refreshToken(): Promise<APIResponse<{ token: string; expires_at: string }>> {
    return this.request<APIResponse<{ token: string; expires_at: string }>>('/api/auth/refresh', {
      method: 'POST',
    })
  }

  // ============ 用户管理API ============

  async getUserProfile(): Promise<APIResponse<UserProfile>> {
    return this.request<APIResponse<UserProfile>>('/api/user/profile')
  }

  async updateUserProfile(updates: UpdateProfileRequest): Promise<APIResponse<UserProfile>> {
    return this.request<APIResponse<UserProfile>>('/api/user/profile', {
      method: 'PUT',
      body: JSON.stringify(updates),
    })
  }

  async getUserQuota(): Promise<APIResponse<UserQuota>> {
    return this.request<APIResponse<UserQuota>>('/api/user/quota')
  }

  async getUserGenerations(page: number = 1, limit: number = 20, mediaType?: string): Promise<APIResponse<HistoryResult>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })

    if (mediaType && mediaType !== 'all') {
      params.append('media_type', mediaType)
    }

    return this.request<APIResponse<HistoryResult>>(`/api/user/generations?${params}`)
  }

  // ============ 内容生成API ============

  async generateImage(prompt: string, seed?: number, model?: string): Promise<GenerationResult> {
    const response = await this.request<GenerationResult>('/api/generate/image', {
      method: 'POST',
      body: JSON.stringify({
        prompt: prompt.trim(),
        ...(seed !== undefined && { seed }),
        ...(model && { model }),
      }),
    })

    return response
  }

  async generateSpeech(
    text: string,
    voice_id: string = 'Deep_Voice_Man',
    emotion: string = 'auto',
    language_boost: string = 'Chinese'
  ): Promise<SpeechResult> {
    const response = await this.request<SpeechResult>('/api/generate/speech', {
      method: 'POST',
      body: JSON.stringify({
        text: text.trim(),
        voice_id,
        emotion,
        language_boost,
      }),
    })

    return response
  }

  async optimizePrompt(prompt: string, model?: string): Promise<{ success: boolean; optimized_prompt: string; original_prompt: string; provider: string; user_id: string }> {
    return this.request('/api/optimize/prompt', {
      method: 'POST',
      body: JSON.stringify({
        prompt: prompt.trim(),
        model: model || 'replicate-openai',
      }),
    })
  }

  // ============ 历史和文件API ============

  async getGenerationHistory(
    page: number = 1,
    limit: number = 20,
    mediaType?: 'image' | 'audio'
  ): Promise<HistoryResult> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })

    if (mediaType) {
      params.append('media_type', mediaType)
    }

    const response = await this.request<HistoryResult>(`/api/user/generations?${params}`)
    return response
  }

  async deleteGeneration(id: string): Promise<APIResponse<{ message: string }>> {
    return this.request<APIResponse<{ message: string }>>(`/api/user/generations/${id}`, {
      method: 'DELETE',
    })
  }

  async setGenerationVisibility(id: string, isPublic: boolean): Promise<APIResponse<{ message: string }>> {
    return this.request<APIResponse<{ message: string }>>(`/api/user/generations/${id}/visibility`, {
      method: 'PUT',
      body: JSON.stringify({ is_public: isPublic }),
    })
  }

  // ============ 管理员API ============

  async getAdminStats(): Promise<APIResponse<any>> {
    return this.request<APIResponse<any>>('/api/admin/stats')
  }

  async getAdminUsers(page: number = 1, limit: number = 20, filters: {
    search?: string
    role?: string
    status?: string
  } = {}): Promise<APIResponse<any>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })

    if (filters.search) params.append('search', filters.search)
    if (filters.role) params.append('role', filters.role)
    if (filters.status) params.append('status', filters.status)

    return this.request<APIResponse<any>>(`/api/admin/users?${params}`)
  }

  async updateUserRole(userId: string, role: 'user' | 'admin' | 'moderator'): Promise<APIResponse<any>> {
    return this.request<APIResponse<any>>(`/api/admin/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    })
  }

  async updateUserQuota(userId: string, quotaLimit: number, resetUsed: boolean = false): Promise<APIResponse<any>> {
    return this.request<APIResponse<any>>(`/api/admin/users/${userId}/quota`, {
      method: 'PUT',
      body: JSON.stringify({ quota_limit: quotaLimit, reset_used: resetUsed }),
    })
  }

  async toggleUserStatus(userId: string, isActive: boolean): Promise<APIResponse<any>> {
    return this.request<APIResponse<any>>(`/api/admin/users/${userId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ is_active: isActive }),
    })
  }

  async createUser(userData: {
    email: string
    password: string
    username?: string
    full_name?: string
    role?: 'user' | 'admin' | 'moderator'
    quota_limit?: number
  }): Promise<APIResponse<any>> {
    return this.request<APIResponse<any>>('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    })
  }

  async resetUserPassword(userId: string): Promise<APIResponse<any>> {
    return this.request<APIResponse<any>>(`/api/admin/users/${userId}/reset-password`, {
      method: 'POST',
    })
  }

  async deleteUser(userId: string): Promise<APIResponse<any>> {
    return this.request<APIResponse<any>>(`/api/admin/users/${userId}`, {
      method: 'DELETE',
    })
  }

  async migrateDatabase(): Promise<APIResponse<any>> {
    return this.request<APIResponse<any>>('/api/admin/migrate', {
      method: 'POST',
    })
  }

  async fixQuotaDecimals(): Promise<APIResponse<any>> {
    return this.request<APIResponse<any>>('/api/admin/fix-quota-decimals', {
      method: 'POST',
    })
  }

  async getMigrationStatus(): Promise<APIResponse<any>> {
    return this.request<APIResponse<any>>('/api/admin/migration-status')
  }

  async getLLMConfig(): Promise<APIResponse<any>> {
    return this.request<APIResponse<any>>('/api/admin/llm-config')
  }

  async getImageModel(): Promise<APIResponse<{ model: string }>> {
    return this.request<APIResponse<{ model: string }>>('/api/config/image-model')
  }

  async updateLLMConfig(config: {
    provider: string
    base_url: string
    api_key: string
    model_name: string
    image_model?: string
  }): Promise<APIResponse<any>> {
    return this.request<APIResponse<any>>('/api/admin/llm-config', {
      method: 'PUT',
      body: JSON.stringify(config),
    })
  }

  async testLLMConfig(config: {
    base_url: string
    api_key: string
    model_name: string
  }): Promise<APIResponse<any>> {
    return this.request<APIResponse<any>>('/api/admin/llm-config/test', {
      method: 'POST',
      body: JSON.stringify(config),
    })
  }

  async getVisionLLMConfig(): Promise<APIResponse<{
    provider: string
    base_url: string
    api_key: string
    model_name: string
  }>> {
    return this.request<APIResponse<{
      provider: string
      base_url: string
      api_key: string
      model_name: string
    }>>('/api/admin/vision-llm-config')
  }

  async updateVisionLLMConfig(config: {
    provider: string
    base_url: string
    api_key: string
    model_name: string
  }): Promise<APIResponse<any>> {
    return this.request<APIResponse<any>>('/api/admin/vision-llm-config', {
      method: 'PUT',
      body: JSON.stringify(config),
    })
  }

  // ============ 图像理解API ============

  async understandImage(imageFile: File): Promise<{
    success: boolean
    id: string
    prompt: string
    image_url: string
    model: string
    provider: string
    user_id: string
    created_at: string
    error?: string
  }> {
    const formData = new FormData()
    formData.append('image', imageFile)

    const { token } = useAuthStore.getState()
    const headers: Record<string, string> = {}

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(`${this.baseUrl}/api/understand/image`, {
      method: 'POST',
      headers,
      body: formData,
    })

    if (!response.ok) {
      let errorMessage = `请求失败: ${response.status}`
      try {
        const errorData = await response.json()
        errorMessage = errorData.error || errorData.message || errorMessage
      } catch {
        // 如果无法解析错误响应，使用默认消息
      }
      throw new Error(errorMessage)
    }

    return await response.json()
  }

  // ============ AI导演工作台API ============

  async getDirectorLLMConfig(): Promise<APIResponse<{
    provider: string
    base_url: string
    api_key: string
    model_name: string
  }>> {
    return this.request<APIResponse<{
      provider: string
      base_url: string
      api_key: string
      model_name: string
    }>>('/api/admin/director-llm-config')
  }

  async updateDirectorLLMConfig(config: {
    provider: string
    base_url: string
    api_key: string
    model_name: string
  }): Promise<APIResponse<any>> {
    return this.request<APIResponse<any>>('/api/admin/director-llm-config', {
      method: 'PUT',
      body: JSON.stringify(config),
    })
  }

  async getAdminDirectorHistory(page: number = 1, limit: number = 50): Promise<APIResponse<{
    records: any[]
    total: number
  }>> {
    return this.request<APIResponse<{
      records: any[]
      total: number
    }>>(`/api/admin/director-history?page=${page}&limit=${limit}`)
  }

  async getDirectorHistory(page: number = 1, limit: number = 20): Promise<APIResponse<{
    records: any[]
    total: number
  }>> {
    return this.request<APIResponse<{
      records: any[]
      total: number
    }>>(`/api/director/history?page=${page}&limit=${limit}`)
  }

  async getDirectorRecord(id: string): Promise<APIResponse<any>> {
    return this.request<APIResponse<any>>(`/api/director/record/${id}`)
  }

  // ============ 工具方法 ============

  getFileUrl(fileId: string): string {
    return `${this.baseUrl}/files/${fileId}`
  }

  getImageUrl(imageId: string): string {
    return this.getFileUrl(imageId)
  }

  getAudioUrl(audioId: string): string {
    return this.getFileUrl(audioId)
  }
}

// 创建全局API客户端实例
export const apiClient = new APIClient()
